// Omnichannel Inbox v2 (PRD §1.7, §4.7 / WP-04).
// Layout:
//   Left: Folder Icon Rail (Mine · All · Important · Unread · Archived · Blocked · Resolved + ⚙)
//   List: Conversation List (320px, search, sort, bot status filter)
//   Center: Thread (header, search, messages, media lightbox, composer with tabs & AI)
//   Right: Context Panel (customer profile, bot/ai toggles, labels, custom fields, notes)
import { useEffect, useState, useRef } from 'react';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import {
  useConversations,
  markConversationRead,
  setConversationFlags,
  type ConversationFolder,
} from '../lib/inbox';
import { playNewMessageChime, showBrowserNotification } from '../lib/notifications';
import InboxIconRail from '../components/inbox/InboxIconRail';
import ConversationList from '../components/inbox/ConversationList';
import ChatThread from '../components/inbox/ChatThread';
import ContextPanel from '../components/inbox/ContextPanel';
import FollowupListModal from '../components/inbox/FollowupListModal';
import AddSubscriberModal from '../components/inbox/AddSubscriberModal';
import InstallAppModal from '../components/inbox/InstallAppModal';
import GroupChatBetaModal from '../components/inbox/GroupChatBetaModal';
import { useToast } from '../components/ui/Toast';

export default function Inbox() {
  const { t } = useI18n();
  const toast = useToast();
  const { conversations } = useConversations();
  const [currentFolder, setCurrentFolder] = useState<ConversationFolder>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showContext, setShowContext] = useState(false);
  const [botOn, setBotOn] = useState(false);
  const [aiOn, setAiOn] = useState(false);

  // Modals state
  const [showFollowupList, setShowFollowupList] = useState(false);
  const [showAddSubscriber, setShowAddSubscriber] = useState(false);
  const [showInstallApp, setShowInstallApp] = useState(false);
  const [showGroupchatBeta, setShowGroupchatBeta] = useState(false);

  const prevUnreadTotalRef = useRef<number | null>(null);

  const selected = conversations.find((c) => c.id === selectedId) ?? null;

  // Keep the toggles in sync with the live conversation doc.
  useEffect(() => {
    const c = conversations.find((x) => x.id === selectedId);
    if (c) {
      setBotOn(c.botReply);
      setAiOn(c.aiAgent);
    }
  }, [selectedId, conversations]);

  // Mark read + reset context ONLY when the selection changes.
  //
  // This must not depend on `conversations`: marking read writes the
  // conversation doc, the snapshot listener delivers a new array, and an
  // effect keyed on it would fire again — an unbounded loop of
  // whatsappMarkConversationRead calls (hundreds per minute, until the browser
  // gives up with ERR_INSUFFICIENT_RESOURCES). Observed live on the client's
  // inbox before this split.
  useEffect(() => {
    setShowContext(false);
    if (selectedId) void markConversationRead({ conversationId: selectedId }).catch(() => {});
  }, [selectedId]);

  // Audio chime & notification on new inbound message
  useEffect(() => {
    const totalUnread = conversations.reduce((acc, c) => acc + (c.unread || 0), 0);
    if (prevUnreadTotalRef.current !== null && totalUnread > prevUnreadTotalRef.current) {
      const soundEnabled = localStorage.getItem('watag.inbox.sound') !== 'false';
      if (soundEnabled) {
        playNewMessageChime();
      }
      // Show browser notification if tab hidden
      if (document.hidden) {
        showBrowserNotification('New WhatsApp Message', {
          body: 'You received a new message in WaTag Omnichannel Inbox.',
        });
      }
    }
    prevUnreadTotalRef.current = totalUnread;
  }, [conversations]);

  // Optimistic toggles that REVERT and tell the user when the server rejects
  // them — previously the switch stayed flipped while nothing had changed.
  function toggleBot(v: boolean) {
    setBotOn(v);
    if (!selectedId) return;
    void setConversationFlags({ conversationId: selectedId, botReply: v }).catch((e) => {
      setBotOn(!v);
      toast.error((e as Error).message || t('common.saveFailed'));
    });
  }
  function toggleAi(v: boolean) {
    setAiOn(v);
    if (!selectedId) return;
    void setConversationFlags({ conversationId: selectedId, aiReply: v }).catch((e) => {
      setAiOn(!v);
      toast.error((e as Error).message || t('common.saveFailed'));
    });
  }

  return (
    <div className="flex h-[calc(100vh-11rem)] min-h-[500px] flex-col md:h-[calc(100vh-8.5rem)]">
      {/* Container Card */}
      <div className="relative flex min-h-0 flex-1 overflow-hidden rounded-card border border-line bg-white dark:border-slate-800 dark:bg-slate-900 shadow-sm transition-colors">
        {/* LEFT ICON RAIL */}
        <div className="shrink-0">
          <InboxIconRail
            currentFolder={currentFolder}
            onSelectFolder={(folder) => {
              setCurrentFolder(folder);
              // On mobile, clearing selectedId brings back conversation list
              if (window.innerWidth < 768) setSelectedId(null);
            }}
            conversations={conversations}
            onOpenFollowupList={() => setShowFollowupList(true)}
            onOpenAddSubscriber={() => setShowAddSubscriber(true)}
            onOpenInstallApp={() => setShowInstallApp(true)}
            onOpenGroupchatBeta={() => setShowGroupchatBeta(true)}
          />
        </div>

        {/* CONVERSATION LIST */}
        <div
          className={cn(
            'w-full flex-col md:flex md:w-[320px] lg:w-[340px] shrink-0 border-e border-line dark:border-slate-800',
            selectedId ? 'hidden md:flex' : 'flex',
          )}
        >
          <ConversationList
            conversations={conversations}
            selectedId={selectedId}
            onSelect={setSelectedId}
            folder={currentFolder}
            onReload={() => {
              // Force focus or trigger refresh
              if (selectedId) void markConversationRead({ conversationId: selectedId }).catch(() => {});
            }}
          />
        </div>

        {/* THREAD PANE */}
        <div
          className={cn(
            'min-w-0 flex-1 flex-col bg-chat-bg dark:bg-slate-950',
            selectedId ? 'flex' : 'hidden md:flex',
          )}
        >
          {selected ? (
            <ChatThread
              key={selected.id}
              conv={selected}
              onBack={() => setSelectedId(null)}
              onInfo={() => setShowContext((v) => !v)}
            />
          ) : (
            <div className="grid flex-1 place-items-center p-8 text-center text-xs text-ink-3 dark:text-slate-500">
              <div className="space-y-2">
                <span className="text-3xl">💬</span>
                <p className="font-bold text-sm text-ink dark:text-slate-300">
                  {t('inbox.emptyTitle') || 'No conversation selected'}
                </p>
                <p>{t('inbox.empty') || 'Select a chat from the left or choose a folder to start messaging.'}</p>
              </div>
            </div>
          )}
        </div>

        {/* CONTEXT PANEL — static column on wide screens (≥1180px) */}
        {selected && (
          <div className="hidden w-[300px] shrink-0 border-s border-line dark:border-slate-800 bg-white dark:bg-slate-900 min-[1180px]:block">
            <ContextPanel
              conv={selected}
              botOn={botOn}
              aiOn={aiOn}
              onBot={toggleBot}
              onAi={toggleAi}
            />
          </div>
        )}

        {/* CONTEXT PANEL — overlay drawer below 1180px */}
        {selected && showContext && (
          <>
            <button
              type="button"
              aria-hidden
              tabIndex={-1}
              onClick={() => setShowContext(false)}
              className="absolute inset-0 z-40 bg-slate-900/40 min-[1180px]:hidden backdrop-blur-xs"
            />
            <div className="absolute inset-y-0 end-0 z-50 flex w-full flex-col border-s border-line dark:border-slate-800 bg-white dark:bg-slate-900 sm:w-[340px] min-[1180px]:hidden shadow-2xl">
              <ContextPanel
                conv={selected}
                botOn={botOn}
                aiOn={aiOn}
                // Same handlers as the wide-screen panel — the mobile drawer
                // used the raw setters and never persisted the toggle.
                onBot={toggleBot}
                onAi={toggleAi}
                onClose={() => setShowContext(false)}
              />
            </div>
          </>
        )}
      </div>

      {/* Dialog Modals */}
      {showFollowupList && (
        <FollowupListModal
          onClose={() => setShowFollowupList(false)}
          onSelectConversation={(cid) => {
            setSelectedId(cid);
            setShowFollowupList(false);
          }}
        />
      )}

      {showAddSubscriber && (
        <AddSubscriberModal
          onClose={() => setShowAddSubscriber(false)}
        />
      )}

      {showInstallApp && (
        <InstallAppModal
          onClose={() => setShowInstallApp(false)}
        />
      )}

      {showGroupchatBeta && (
        <GroupChatBetaModal
          onClose={() => setShowGroupchatBeta(false)}
        />
      )}
    </div>
  );
}
